import { GetDashboardUpcomingSessionModel } from "@factory/model/query-model-dashboard";
import { currencyFormat } from "@shared/utility/formats/currency-format";
import { Column, ColumnBodyOptions } from "primereact/column";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Tag } from "primereact/tag";

import {
  formatSessionDate,
  formatSessionTime,
  getSessionStatusDisplay,
} from "../upcoming-session.util";

export interface UpcomingSessionTableProps {
  rows: GetDashboardUpcomingSessionModel[];
  // Row number of the first row, so "#" keeps counting across pages
  startIndex?: number;
  sortable?: boolean;
  sortField?: string;
  sortOrder?: 1 | 0 | -1 | null;
  emptyMessage?: string;
  onSort?: (event: DataTableStateEvent) => void;
  onRowSelect?: (row: GetDashboardUpcomingSessionModel) => void;
}

export function UpcomingSessionTable({
  rows,
  startIndex = 0,
  sortable = false,
  sortField,
  sortOrder,
  emptyMessage = "No upcoming sessions.",
  onSort,
  onRowSelect,
}: UpcomingSessionTableProps) {
  const dateBody = (row: GetDashboardUpcomingSessionModel) => {
    const time = formatSessionTime(row.time);
    return (
      <div className="flex flex-col">
        <span>{formatSessionDate(row.date)}</span>
        {time && <span className="text-xs text-gray-500">{time}</span>}
      </div>
    );
  };

  const statusBody = (row: GetDashboardUpcomingSessionModel) => {
    const { label, severity } = getSessionStatusDisplay(row.status);
    return (
      <Tag
        value={label}
        severity={severity}
        className={severity ? undefined : "bg-gray-400"}
      />
    );
  };

  return (
    <DataTable
      value={rows}
      dataKey="visit"
      size="small"
      emptyMessage={emptyMessage}
      selectionMode={onRowSelect ? "single" : undefined}
      onSelectionChange={
        onRowSelect
          ? (e: { value: unknown }) =>
              onRowSelect(e.value as GetDashboardUpcomingSessionModel)
          : undefined
      }
      onSort={onSort}
      sortField={sortField}
      sortOrder={sortOrder}
      // Rows arrive already ordered by the API
      lazy
      className="upcoming-session-table"
    >
      <Column
        header="#"
        body={(_: unknown, options: ColumnBodyOptions) =>
          startIndex + options.rowIndex + 1
        }
        style={{ width: "4rem" }}
      />
      <Column
        field="service_name"
        header="Service"
        sortable={sortable}
        body={(row) => row.service_name || "-"}
      />
      <Column field="client_name" header="Client Name" sortable={sortable} />
      <Column field="date" header="Date" sortable={sortable} body={dateBody} />
      <Column
        field="session"
        header="Session"
        sortable={sortable}
        body={(row) => `Session #${row.session}`}
      />
      <Column
        field="expected_payment"
        header="Expected Payment"
        sortable={sortable}
        body={(row) => currencyFormat(row.expected_payment ?? 0)}
      />
      <Column field="status" header="Status" body={statusBody} />
    </DataTable>
  );
}

export default UpcomingSessionTable;
